import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Search,
  Calendar,
  MapPin,
  Users,
  Target,
  ArrowRight,
  Eye,
  Download,
} from "lucide-react";
import SecondaryNavbar from "../../components/common/SecondaryNavbar";
import SecondaryHero from "../../components/common/SecondaryHero";

const ProjectsPage = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);

  // Project categories based on your data
  const categories = [
    { id: "all", name: "All Projects", count: 25 },
    { id: "forest-clearance", name: "Forest Clearance", count: 5 },
    { id: "dgps-survey", name: "DGPS Survey", count: 5 },
    { id: "total-station", name: "Total Station", count: 2 },
    { id: "gis-mapping", name: "GIS Mapping", count: 2 },
    { id: "land-use", name: "Land Use", count: 2 },
    { id: "water-supply", name: "Water Supply", count: 2 },
    { id: "railway-mapping", name: "Railway Mapping", count: 2 },
    { id: "forest-division", name: "Forest Division", count: 2 },
    { id: "fire-eco", name: "Fire & Eco", count: 2 },
    { id: "stock-measurement", name: "Stock Measurement", count: 1 },
  ];

  // Project data with images from Unsplash
  const projects = [
    {
      id: 1,
      title: "Forest Clearance for Construction of Intake Well and Electrical Building near Chhamundia",
      category: "forest-clearance",
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80",
      client: "Gania Block, Nayagarh District",
      location: "Nayagarh District, Odisha",
      duration: "8 Months",
      completion: "2023",
      description: "Forest clearance for Mega PWS in 11 Blocks of Ganjam District including five years of Operation and Maintenance on EPC contract",
      technologies: ["DGPS", "GIS Mapping", "Remote Sensing", "Environmental Assessment"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80",
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80"
      ],
    },
    {
      id: 2,
      title: "DGPS Survey for Forest Clearance - Chilika Scheme WTP",
      category: "forest-clearance",
      image: "https://images.unsplash.com/photo-1470115636492-6d2b56f9146d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Khordha District Administration",
      location: "Khordha District, Odisha",
      duration: "6 Months",
      completion: "2023",
      description: "DGPS survey required for forest clearance for 02 Nos. Individual Rural Piped Water Supply Projects in Chilika, Tangi, Banapur & Khordha blocks",
      technologies: ["DGPS", "Total Station", "GIS Analysis", "Water Network"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1470115636492-6d2b56f9146d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    },
    {
      id: 3,
      title: "Forest Diversion Proposal for 3x800 MW OTEL USCTPP",
      category: "forest-clearance",
      image: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Orissa Thermal Energy Ltd.",
      location: "Cuttack, Odisha",
      duration: "12 Months",
      completion: "2024",
      description: "DGPS Survey for FDP & Wildlife clearance for 3x800 MW thermal power plant as per MoEF & CC Guidelines & Forest (Conservation) Act 1980",
      technologies: ["DGPS", "Wildlife Assessment", "Environmental Impact", "Thermal Mapping"],
      status: "Ongoing",
      progress: 75,
      gallery: [
        "https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80"
      ],
    },
    {
      id: 4,
      title: "Railway Corridor Survey for 2400 MW Nilanchal Thermal Power Plant",
      category: "forest-clearance",
      image: "https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Orissa Thermal Energy Limited",
      location: "Athagarh, Cuttack, Odisha",
      duration: "10 Months",
      completion: "2023",
      description: "DGPS survey for railway corridor of proposed 2400 MW Coal Based Ultra Super Critical Thermal Power Plant",
      technologies: ["DGPS", "Railway Alignment", "Terrain Analysis", "Corridor Planning"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80"
      ],
    },
    {
      id: 5,
      title: "UPPER JONK Irrigation Project Reservoir Survey",
      category: "forest-clearance",
      image: "https://images.unsplash.com/photo-1563974318767-a4de855d7b43?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80",
      client: "Water Resources Department",
      location: "Nuapada, Odisha",
      duration: "7 Months",
      completion: "2023",
      description: "DGPS survey for raising reservoir level FRL from 350.6m to 354.1m for forest diversion proposal",
      technologies: ["DGPS", "Hydrological Survey", "Reservoir Mapping", "Water Resources"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1563974318767-a4de855d7b43?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80",
        "https://images.unsplash.com/photo-1509316785289-025f5b846b35?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80"
      ],
    },
    {
      id: 6,
      title: "DGPS Survey for Narayani & Sons Pvt. Ltd.",
      category: "dgps-survey",
      image: "https://images.unsplash.com/photo-1586769852836-bc069f19e1b6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Narayani & Sons Pvt. Ltd.",
      location: "Barbil, Keonjhar",
      duration: "3 Months",
      completion: "2023",
      description: "Boundary survey and mapping for mining operations in Barbil region",
      technologies: ["DGPS", "Boundary Demarcation", "CAD Mapping", "Mining Survey"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1586769852836-bc069f19e1b6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    },
    {
      id: 7,
      title: "Urumunda Iron Ore Mines Survey",
      category: "dgps-survey",
      image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Urumunda Mines",
      location: "Keonjhar, Odisha",
      duration: "4 Months",
      completion: "2023",
      description: "Comprehensive DGPS and Total Station survey for mining lease area with 3D modeling",
      technologies: ["DGPS", "Total Station", "3D Modeling", "Mine Planning"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1574482620811-1aa16ffe3c82?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    },
    {
      id: 8,
      title: "NH-215 Widening Survey",
      category: "dgps-survey",
      image: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80",
      client: "NHAI",
      location: "Panikoili to Rimuli (163 Km)",
      duration: "5 Months",
      completion: "2023",
      description: "Detailed survey for widening of National Highway 215 with topographic analysis",
      technologies: ["DGPS", "Road Alignment", "Topographic Survey", "Highway Engineering"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80",
        "https://images.unsplash.com/photo-1542224566-6e85f2e6772f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    },
    {
      id: 9,
      title: "IIT Bhubaneswar Campus Survey",
      category: "dgps-survey",
      image: "https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=2064&q=80",
      client: "IIT Bhubaneswar",
      location: "Argul, Jatani, Khurda",
      duration: "6 Months",
      completion: "2023",
      description: "Survey of forest land proposed for diversion for establishment of Permanent Campus of IIT Bhubaneswar",
      technologies: ["DGPS", "Campus Planning", "Site Analysis", "Educational Infrastructure"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=2064&q=80",
        "https://images.unsplash.com/photo-1561154464-82e9adf32764?ixlib=rb-4.0.3&auto=format&fit=crop&w=2067&q=80"
      ],
    },
    {
      id: 10,
      title: "Thakurani Iron Ore Mines Boundary Survey",
      category: "dgps-survey",
      image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80",
      client: "ArcelorMittal India Private Limited",
      location: "Barbil, Keonjhar",
      duration: "4 Months",
      completion: "2023",
      description: "ML Boundary Survey and GIS mapping for 82.018 Ha forest area in Thakurani region",
      technologies: ["DGPS", "GIS", "Spatial Analysis", "Boundary Survey"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80",
        "https://images.unsplash.com/photo-1574482620811-1aa16ffe3c82?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    },
    {
      id: 11,
      title: "SAIL-RMD Manoharpur Ore Mines Survey",
      category: "total-station",
      image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "SAIL-RMD",
      location: "Chiria, Jharkhand",
      duration: "8 Months",
      completion: "2023",
      description: "Completed Total Station and DGPS survey of 15.333 km Ropecon conveyor route boundary with 3D modeling",
      technologies: ["Total Station", "DGPS", "Conveyor Alignment", "3D Modeling"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1574482620811-1aa16ffe3c82?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    },
    {
      id: 12,
      title: "Road Survey for Rural Works Division",
      category: "total-station",
      image: "https://images.unsplash.com/photo-1542224566-6e85f2e6772f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Rural Works Division",
      location: "Rambha, Balugaon",
      duration: "3 Months",
      completion: "2023",
      description: "ETS survey for rural road development projects in coastal Odisha region",
      technologies: ["Total Station", "Road Design", "Cross-section Survey", "Rural Infrastructure"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1542224566-6e85f2e6772f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80"
      ],
    },
    {
      id: 13,
      title: "NTFP Inventory Mapping",
      category: "gis-mapping",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80",
      client: "Forest Department",
      location: "Multiple Forest Divisions",
      duration: "12 Months",
      completion: "2023",
      description: "Customized tool for NTFP inventory work across 8 forest divisions with mobile GIS integration",
      technologies: ["ArcGIS", "Web GIS", "Database Management", "Mobile Mapping", "NTFP Inventory"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80",
        "https://images.unsplash.com/photo-1464822759844-4c9a052f93be?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80"
      ],
    },
    {
      id: 14,
      title: "Forest Division GPS Surveys",
      category: "gis-mapping",
      image: "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80",
      client: "Various Forest Divisions",
      location: "Across Odisha",
      duration: "24 Months",
      completion: "2024",
      description: "GPS Survey for Treatment Areas, JFM, NREGS, CAMPA, FDA, 13th F.C, Watershed Mapping across 19 forest divisions",
      technologies: ["GPS", "JUNO", "Mobile GIS", "Field Data Collection", "Watershed Mapping"],
      status: "Ongoing",
      progress: 85,
      gallery: [
        "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80",
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80"
      ],
    },
    {
      id: 15,
      title: "Adani Mining Land Use Classification",
      category: "land-use",
      image: "https://images.unsplash.com/photo-1509316785289-025f5b846b35?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80",
      client: "Adani Mining Private Limited",
      location: "Various Locations",
      duration: "6 Months",
      completion: "2023",
      description: "Comprehensive land use classification and cadastral mapping for mining operations",
      technologies: ["Remote Sensing", "Land Use Classification", "Cadastral Mapping", "Digitization", "Mining GIS"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1509316785289-025f5b846b35?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80",
        "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    },
    {
      id: 16,
      title: "Hindalco Industries Cadastral Mapping",
      category: "land-use",
      image: "https://images.unsplash.com/photo-1574482620811-1aa16ffe3c82?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Hindalco Industries Ltd.",
      location: "Aditya Aluminium Unit",
      duration: "5 Months",
      completion: "2023",
      description: "Detailed cadastral mapping and boundary demarcation for industrial operations",
      technologies: ["GIS", "Cadastral Survey", "Boundary Demarcation", "Legal Mapping", "Industrial Planning"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1574482620811-1aa16ffe3c82?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80"
      ],
    },
    {
      id: 17,
      title: "Water Supply Map & DPR - Khallikote",
      category: "water-supply",
      image: "https://t4.ftcdn.net/jpg/10/46/56/19/360_F_1046561916_VrDhIm6SUayyx9djfgQh81RwUHp2NwzE.jpg",
      client: "PHED",
      location: "Khallikote, Odisha",
      duration: "4 Months",
      completion: "2023",
      description: "Complete water supply mapping and detailed project report preparation for rural water supply",
      technologies: ["Water Network Mapping", "Hydraulic Modeling", "DPR Preparation", "GIS", "Rural Water Supply"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSpfdPGGeeRYUxcDY-BZpU0-BnC9LTzBzTxkA&s",
        "https://t4.ftcdn.net/jpg/11/86/20/37/360_F_1186203769_LOYtXf009rr0rqOnYJMhOCxJA66zdQGY.jpg"
      ],
    },
    {
      id: 18,
      title: "Multi-location Water Supply Projects",
      category: "water-supply",
      image: "https://kalyangroup.in/wp-content/uploads/2024/01/pro-58.jpg",
      client: "PHED",
      location: "Khariar, Aska, Chhatrapur, Bhanjanagar",
      duration: "6 Months",
      completion: "2023",
      description: "Water supply mapping for multiple locations across Odisha with infrastructure planning",
      technologies: ["Network Analysis", "GIS Mapping", "DPR", "Infrastructure Planning", "Multi-location Survey"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://kalyangroup.in/wp-content/uploads/2024/01/pro-58.jpg",
        "https://kalyangroup.in/wp-content/uploads/2024/01/pro-58.jpg",
      ],
    },
    {
      id: 19,
      title: "Railway Siding Maps - Shyam Steel",
      category: "railway-mapping",
      image: "https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Shyam Steel & Energy Ltd.",
      location: "Sambalpur District",
      duration: "3 Months",
      completion: "2023",
      description: "Detailed railway siding mapping for industrial connectivity and logistics planning",
      technologies: ["Railway Alignment", "Siding Design", "GIS Mapping", "Cross-section", "Industrial Logistics"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80"
      ],
    },
    {
      id: 20,
      title: "Rashmi Metalliks Railway Siding",
      category: "railway-mapping",
      image: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80",
      client: "Rashmi Metalliks Pvt. Ltd.",
      location: "Industrial Area",
      duration: "3 Months",
      completion: "2023",
      description: "Railway siding mapping and connectivity planning for steel plant operations",
      technologies: ["Railway Engineering", "GIS", "Infrastructure Planning", "Steel Plant Logistics"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80",
        "https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    },
    {
      id: 21,
      title: "Ghumsur South Forest Division Mapping",
      category: "forest-division",
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80",
      client: "Forest Department",
      location: "Bhanjanagar Division",
      duration: "5 Months",
      completion: "2023",
      description: "Complete division mapping including compartments, beats, and ranges for forest management",
      technologies: ["Forest Mapping", "Compartment Mapping", "GIS Database", "Management Plans", "Forest Inventory"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80",
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80"
      ],
    },
    {
      id: 22,
      title: "Multi-Division Forest Maps",
      category: "forest-division",
      image: "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80",
      client: "Forest Department",
      location: "Deogarh, Boudh, Balliguda, Jeypore",
      duration: "8 Months",
      completion: "2023",
      description: "Comprehensive mapping of 5 forest divisions with spatial database and web mapping",
      technologies: ["GIS", "Forest Inventory", "Spatial Database", "Web Mapping", "Multi-division Planning"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=2076&q=80",
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80"
      ],
    },
    {
      id: 23,
      title: "Balliguda Range Fire Mapping",
      category: "fire-eco",
      image: "https://images.unsplash.com/photo-1506784365847-bbad939e9335?ixlib=rb-4.0.3&auto=format&fit=crop&w=2068&q=80",
      client: "Forest Department",
      location: "Balliguda Range",
      duration: "4 Months",
      completion: "2023",
      description: "Forest fire risk mapping and management planning with GIS analysis and risk assessment",
      technologies: ["Fire Risk Modeling", "GIS Analysis", "Remote Sensing", "Risk Assessment", "Forest Fire Management"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1506784365847-bbad939e9335?ixlib=rb-4.0.3&auto=format&fit=crop&w=2068&q=80",
        "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80"
      ],
    },
    {
      id: 24,
      title: "Satakosia Eco-Sensitive Zone",
      category: "fire-eco",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Wildlife Department",
      location: "Satakosia Tiger Reserve",
      duration: "5 Months",
      completion: "2023",
      description: "Eco-sensitive zone mapping and buffer zone delineation for wildlife conservation",
      technologies: ["ESZ Mapping", "Wildlife Corridor", "Buffer Zone", "Conservation Planning", "Tiger Reserve"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1506784365847-bbad939e9335?ixlib=rb-4.0.3&auto=format&fit=crop&w=2068&q=80"
      ],
    },
    {
      id: 25,
      title: "Adhunik Metaliks Stock Measurement",
      category: "stock-measurement",
      image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      client: "Adhunik Metaliks Limited",
      location: "Kuarmunda, Sundargarh",
      duration: "2 Months",
      completion: "2023",
      description: "Comprehensive stock measurement and volume assessment for mining operations with 3D modeling",
      technologies: ["Volume Calculation", "3D Modeling", "Drone Survey", "Stock Assessment", "Mine Stockpile"],
      status: "Completed",
      progress: 100,
      gallery: [
        "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
      ],
    }
  ];

  // Filter projects
  const filteredProjects = projects.filter((project) => {
    const matchesCategory =
      selectedCategory === "all" || project.category === selectedCategory;
    const matchesSearch =
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.technologies.some((tech) =>
        tech.toLowerCase().includes(searchQuery.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6,
      },
    },
  };

  // Calculate project counts for each category
  const getCategoryCounts = () => {
    const counts = {};
    projects.forEach(project => {
      counts[project.category] = (counts[project.category] || 0) + 1;
    });
    return counts;
  };

  const categoryCounts = getCategoryCounts();

  return (
    <div className='overflow-hidden'>
      <SecondaryNavbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <SecondaryHero
          pageName="Our Portfolio"
          pageHead1="Successful"
          pageHead2=""
          pageHead3=""
          pageHead4="Projects"
          pageDesc="Discover how we've helped clients achieve their goals with innovative GIS, surveying, and mapping solutions across Odisha and beyond."
          bgImgUrl="https://store-images.s-microsoft.com/image/apps.4083.14191174512742283.25b58903-5b4d-47c0-959c-c79a5aabf46c.17e473bf-b171-4bc5-b00e-f1a6f54977e1"
        />

        {/* Projects Content */}
        <section ref={sectionRef} className="py-6 lg:py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Statistics Banner */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6 }}
              className="mb-8 p-6 bg-gradient-to-r from-brandBlue/10 to-brandGreen/10 rounded-2xl border border-brandBlue/20"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-brandBlue">{projects.length}</div>
                  <div className="text-secondaryTextColor">Total Projects</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {projects.filter(p => p.status === "Completed").length}
                  </div>
                  <div className="text-secondaryTextColor">Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-500">
                    {projects.filter(p => p.status === "Ongoing").length}
                  </div>
                  <div className="text-secondaryTextColor">Ongoing</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-brandGreen">{categories.length - 1}</div>
                  <div className="text-secondaryTextColor">Categories</div>
                </div>
              </div>
            </motion.div>

            {/* Filters and Search */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex flex-col lg:flex-row gap-6 mb-8">
                {/* Search Bar */}
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search projects by title, description, client, or technology..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
                  />
                </div>
              </div>

              {/* Category Filters - Scrollable */}
              <div className="overflow-x-auto pb-4">
                <div className="flex gap-3 min-w-max pb-2">
                  {categories.map((category) => (
                    <motion.button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-300 whitespace-nowrap ${
                        selectedCategory === category.id
                          ? "bg-brandBlue text-white shadow-lg"
                          : "bg-white text-gray-700 border border-gray-300 hover:border-brandBlue hover:text-brandBlue"
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {category.name} ({categoryCounts[category.id] || category.count})
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Projects Grid */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  variants={itemVariants}
                  className="group bg-white rounded-3xl border border-gray-200 hover:border-brandBlue/30 transition-all duration-500 hover:shadow-2xl overflow-hidden"
                >
                  {/* Project Image */}
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />

                    {/* Overlay Actions */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center space-x-3 opacity-0 group-hover:opacity-100">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setSelectedProject(project)}
                        className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:text-brandBlue transition-all duration-300"
                      >
                        <Eye className="w-5 h-5" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:text-brandBlue transition-all duration-300"
                      >
                        <Download className="w-5 h-5" />
                      </motion.button>
                    </div>

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full text-white text-xs font-semibold backdrop-blur-sm bg-black/60">
                        {
                          categories.find((cat) => cat.id === project.category)
                            ?.name
                        }
                      </span>
                    </div>

                    {/* Status Badge */}
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-white text-xs font-semibold backdrop-blur-sm ${
                        project.status === "Completed" ? "bg-green-500/90" : "bg-orange-500/90"
                      }`}>
                        {project.status}
                      </span>
                    </div>

                    {/* Progress Bar for Ongoing Projects */}
                    {project.status === "Ongoing" && (
                      <div className="absolute bottom-0 left-0 right-0 h-2 bg-gray-200">
                        <div 
                          className="h-full bg-brandBlue transition-all duration-500"
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    )}
                  </div>

                  {/* Project Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-primaryTextColor mb-3 font-lexend group-hover:text-brandBlue transition-colors duration-300 line-clamp-2">
                      {project.title}
                    </h3>

                    <p className="text-secondaryTextColor leading-relaxed mb-4 line-clamp-3">
                      {project.description}
                    </p>

                    {/* Project Meta */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center space-x-3 text-sm text-secondaryTextColor">
                        <Users className="w-4 h-4" />
                        <span className="truncate">{project.client}</span>
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-secondaryTextColor">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{project.location}</span>
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-secondaryTextColor">
                        <Calendar className="w-4 h-4" />
                        <span>{project.completion} • {project.duration}</span>
                      </div>
                    </div>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies
                        .slice(0, 3)
                        .map((tech, techIndex) => (
                          <span
                            key={techIndex}
                            className="px-3 py-1 bg-gray-100 text-secondaryTextColor text-xs font-medium rounded-lg"
                          >
                            {tech}
                          </span>
                        ))}
                      {project.technologies.length > 3 && (
                        <span className="px-3 py-1 bg-gray-100 text-secondaryTextColor text-xs font-medium rounded-lg">
                          +{project.technologies.length - 3} more
                        </span>
                      )}
                    </div>

                    {/* CTA Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedProject(project)}
                      className="w-full py-3 hover:bg-brandGreen text-white font-semibold rounded-xl bg-brandBlue transition-all duration-300 flex items-center justify-center space-x-2 group/btn"
                    >
                      <span>View Project Details</span>
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Empty State */}
            {filteredProjects.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center py-16"
              >
                <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <Search className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-primaryTextColor mb-4">
                  No projects found
                </h3>
                <p className="text-secondaryTextColor max-w-md mx-auto">
                  Try adjusting your search criteria or select a different category to find what you're looking for.
                </p>
                <button
                  onClick={() => {
                    setSelectedCategory("all");
                    setSearchQuery("");
                  }}
                  className="mt-6 px-6 py-3 bg-brandBlue text-white font-semibold rounded-xl hover:bg-brandBlue/90 transition-all duration-300"
                >
                  View All Projects
                </button>
              </motion.div>
            )}
          </div>
        </section>

        {/* Project Modal - Keep the existing modal code */}
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Modal Header */}
              <div className="relative">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-64 object-cover rounded-t-3xl"
                />
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:text-brandBlue transition-all duration-300"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <span className="px-3 py-1 bg-brandBlue text-white text-sm font-semibold rounded-full">
                    {
                      categories.find(
                        (cat) => cat.id === selectedProject.category
                      )?.name
                    }
                  </span>
                  <span className={`px-3 py-1 text-white text-sm font-semibold rounded-full ${
                    selectedProject.status === "Completed" ? "bg-green-500" : "bg-orange-500"
                  }`}>
                    {selectedProject.status}
                  </span>
                </div>

                <h2 className="text-3xl font-bold text-primaryTextColor mb-4 font-lexend">
                  {selectedProject.title}
                </h2>

                <p className="text-secondaryTextColor text-lg leading-relaxed mb-6">
                  {selectedProject.description}
                </p>

                {/* Project Details Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-brandBlue" />
                      <div>
                        <div className="text-sm text-secondaryTextColor">
                          Client
                        </div>
                        <div className="font-semibold text-primaryTextColor">
                          {selectedProject.client}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-brandBlue" />
                      <div>
                        <div className="text-sm text-secondaryTextColor">
                          Location
                        </div>
                        <div className="font-semibold text-primaryTextColor">
                          {selectedProject.location}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-brandBlue" />
                      <div>
                        <div className="text-sm text-secondaryTextColor">
                          Duration
                        </div>
                        <div className="font-semibold text-primaryTextColor">
                          {selectedProject.duration}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Target className="w-5 h-5 text-brandBlue" />
                      <div>
                        <div className="text-sm text-secondaryTextColor">
                          Completed
                        </div>
                        <div className="font-semibold text-primaryTextColor">
                          {selectedProject.completion}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Technologies */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-primaryTextColor mb-4 font-lexend">
                    Technologies Used
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-gray-100 text-secondaryTextColor font-medium rounded-xl"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Gallery Preview */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-primaryTextColor mb-4 font-lexend">
                    Project Gallery
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedProject.gallery.slice(0, 2).map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`${selectedProject.title} ${index + 1}`}
                        className="w-full h-32 object-cover rounded-xl"
                      />
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-3 bg-brandBlue text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download Case Study</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSelectedCategory(selectedProject.category);
                      setSelectedProject(null);
                    }}
                    className="px-6 py-3 bg-white text-brandBlue font-semibold rounded-xl border-2 border-brandBlue hover:bg-brandBlue hover:text-white transition-all duration-300"
                  >
                    Similar Projects
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;





// import React, { useState, useRef } from "react";
// import { motion, useInView } from "framer-motion";
// import {
//   Search,
//   Calendar,
//   MapPin,
//   Users,
//   Target,
//   ArrowRight,
//   Eye,
//   Download,
// } from "lucide-react";
// import SecondaryNavbar from "../../components/common/SecondaryNavbar";
// import SecondaryHero from "../../components/common/SecondaryHero";

// const ProjectsPage = () => {
//   const sectionRef = useRef(null);
//   const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
//   const [selectedCategory, setSelectedCategory] = useState("all");
//   const [searchQuery, setSearchQuery] = useState("");
//   const [selectedProject, setSelectedProject] = useState(null);

//   // Project categories
//   const categories = [
//     { id: "all", name: "All Projects", count: 12 },
//     { id: "gis", name: "GIS Mapping", count: 4 },
//     { id: "drone", name: "Drone Surveys", count: 3 },
//     { id: "environment", name: "Environmental", count: 2 },
//     { id: "mining", name: "Mining", count: 2 },
//     { id: "urban", name: "Urban Planning", count: 1 },
//   ];

//   // Real project data with actual images from Unsplash
//   const projects = []

//   // Filter projects
//   const filteredProjects = projects.filter((project) => {
//     const matchesCategory =
//       selectedCategory === "all" || project.category === selectedCategory;
//     const matchesSearch =
//       project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
//       project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
//       project.technologies.some((tech) =>
//         tech.toLowerCase().includes(searchQuery.toLowerCase())
//       );
//     return matchesCategory && matchesSearch;
//   });

//   const containerVariants = {
//     hidden: { opacity: 0 },
//     visible: {
//       opacity: 1,
//       transition: {
//         staggerChildren: 0.1,
//       },
//     },
//   };

//   const itemVariants = {
//     hidden: { opacity: 0, y: 30 },
//     visible: {
//       opacity: 1,
//       y: 0,
//       transition: {
//         type: "spring",
//         stiffness: 100,
//         damping: 15,
//         duration: 0.6,
//       },
//     },
//   };

//   return (
//     <div className='overflow-hidden'>
//       <SecondaryNavbar />
//       <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
//         <SecondaryHero
//           pageName="Our Portfolio"
//           pageHead1="Successful"
//           pageHead2=""
//           pageHead3=""
//           pageHead4="Projects"
//           pageDesc="Discover how we've helped clients achieve their goals with innovative GIS and mapping solutions."
//           bgImgUrl="https://store-images.s-microsoft.com/image/apps.4083.14191174512742283.25b58903-5b4d-47c0-959c-c79a5aabf46c.17e473bf-b171-4bc5-b00e-f1a6f54977e1"
//         />

//         {/* Projects Content */}
//         <section ref={sectionRef} className="py-6 lg:py-12">
//           <div className="container mx-auto px-4 sm:px-6 lg:px-8">
//             {/* Filters and Search */}
//             <motion.div
//               initial={{ opacity: 0, y: 30 }}
//               animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
//               transition={{ duration: 0.6 }}
//               className="mb-12"
//             >
//               <div className="flex flex-col lg:flex-row gap-6 mb-8">
//                 {/* Search Bar */}
//                 <div className="flex-1 relative">
//                   <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
//                   <input
//                     type="text"
//                     placeholder="Search projects..."
//                     value={searchQuery}
//                     onChange={(e) => setSearchQuery(e.target.value)}
//                     className="w-full pl-12 pr-4 py-4 bg-white border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
//                   />
//                 </div>
//               </div>

//               {/* Category Filters */}
//               <div className="flex flex-wrap gap-3 justify-center">
//                 {categories.map((category) => (
//                   <motion.button
//                     key={category.id}
//                     onClick={() => setSelectedCategory(category.id)}
//                     className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
//                       selectedCategory === category.id
//                         ? "bg-brandBlue text-white shadow-lg"
//                         : "bg-white text-gray-700 border border-gray-300 hover:border-brandBlue hover:text-brandBlue"
//                     }`}
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                   >
//                     {category.name} ({category.count})
//                   </motion.button>
//                 ))}
//               </div>
//             </motion.div>

//             {/* Projects Grid */}
//             <motion.div
//               variants={containerVariants}
//               initial="hidden"
//               animate={isInView ? "visible" : "hidden"}
//               className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
//             >
//               {filteredProjects.map((project, index) => (
//                 <motion.div
//                   key={project.id}
//                   variants={itemVariants}
//                   className="group bg-white rounded-3xl border border-gray-200 hover:border-brandBlue/30 transition-all duration-500 hover:shadow-2xl overflow-hidden"
//                 >
//                   {/* Project Image */}
//                   <div className="relative aspect-video overflow-hidden">
//                     <img
//                       src={project.image}
//                       alt={project.title}
//                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
//                     />

//                     {/* Overlay Actions */}
//                     <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center space-x-3 opacity-0 group-hover:opacity-100">
//                       <motion.button
//                         whileHover={{ scale: 1.1 }}
//                         whileTap={{ scale: 0.9 }}
//                         onClick={() => setSelectedProject(project)}
//                         className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:text-brandBlue transition-all duration-300"
//                       >
//                         <Eye className="w-5 h-5" />
//                       </motion.button>
//                       <motion.button
//                         whileHover={{ scale: 1.1 }}
//                         whileTap={{ scale: 0.9 }}
//                         className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:text-brandBlue transition-all duration-300"
//                       >
//                         <Download className="w-5 h-5" />
//                       </motion.button>
//                     </div>

//                     {/* Category Badge */}
//                     <div className="absolute top-4 left-4">
//                       <span className="px-3 py-1 rounded-full text-white text-xs font-semibold backdrop-blur-sm bg-black/60">
//                         {
//                           categories.find((cat) => cat.id === project.category)
//                             ?.name
//                         }
//                       </span>
//                     </div>

//                     {/* Status Badge */}
//                     <div className="absolute top-4 right-4">
//                       <span className="px-3 py-1 rounded-full text-white text-xs font-semibold backdrop-blur-sm bg-green-500/90">
//                         {project.status}
//                       </span>
//                     </div>
//                   </div>

//                   {/* Project Content */}
//                   <div className="p-6">
//                     <h3 className="text-xl font-bold text-primaryTextColor mb-3 font-lexend group-hover:text-brandBlue transition-colors duration-300 line-clamp-2">
//                       {project.title}
//                     </h3>

//                     <p className="text-secondaryTextColor leading-relaxed mb-4 line-clamp-3">
//                       {project.description}
//                     </p>

//                     {/* Project Meta */}
//                     <div className="space-y-3 mb-4">
//                       <div className="flex items-center space-x-3 text-sm text-secondaryTextColor">
//                         <Users className="w-4 h-4" />
//                         <span>{project.client}</span>
//                       </div>
//                       <div className="flex items-center space-x-3 text-sm text-secondaryTextColor">
//                         <MapPin className="w-4 h-4" />
//                         <span>{project.location}</span>
//                       </div>
//                       <div className="flex items-center space-x-3 text-sm text-secondaryTextColor">
//                         <Calendar className="w-4 h-4" />
//                         <span>{project.completion}</span>
//                       </div>
//                     </div>

//                     {/* Technologies */}
//                     <div className="flex flex-wrap gap-2 mb-4">
//                       {project.technologies
//                         .slice(0, 3)
//                         .map((tech, techIndex) => (
//                           <span
//                             key={techIndex}
//                             className="px-3 py-1 bg-gray-100 text-secondaryTextColor text-xs font-medium rounded-lg"
//                           >
//                             {tech}
//                           </span>
//                         ))}
//                       {project.technologies.length > 3 && (
//                         <span className="px-3 py-1 bg-gray-100 text-secondaryTextColor text-xs font-medium rounded-lg">
//                           +{project.technologies.length - 3} more
//                         </span>
//                       )}
//                     </div>

//                     {/* CTA Button */}
//                     <motion.button
//                       whileHover={{ scale: 1.02 }}
//                       whileTap={{ scale: 0.98 }}
//                       onClick={() => setSelectedProject(project)}
//                       className="w-full py-3 hover:bg-brandGreen text-white font-semibold rounded-xl bg-brandBlue transition-all duration-300 flex items-center justify-center space-x-2 group/btn"
//                     >
//                       <span>View Project Details</span>
//                       <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
//                     </motion.button>
//                   </div>
//                 </motion.div>
//               ))}
//             </motion.div>

//             {/* Empty State */}
//             {filteredProjects.length === 0 && (
//               <motion.div
//                 initial={{ opacity: 0, y: 30 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ duration: 0.6 }}
//                 className="text-center py-16"
//               >
//                 <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
//                   <Search className="w-10 h-10 text-gray-400" />
//                 </div>
//                 <h3 className="text-2xl font-bold text-primaryTextColor mb-4">
//                   No projects found
//                 </h3>
//                 <p className="text-secondaryTextColor max-w-md mx-auto">
//                   Try adjusting your search criteria or browse all categories to
//                   find what you're looking for.
//                 </p>
//               </motion.div>
//             )}
//           </div>
//         </section>

//         {/* Project Modal */}
//         {selectedProject && (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
//             onClick={() => setSelectedProject(null)}
//           >
//             <motion.div
//               initial={{ scale: 0.9, opacity: 0 }}
//               animate={{ scale: 1, opacity: 1 }}
//               exit={{ scale: 0.9, opacity: 0 }}
//               onClick={(e) => e.stopPropagation()}
//               className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
//             >
//               {/* Modal Header */}
//               <div className="relative">
//                 <img
//                   src={selectedProject.image}
//                   alt={selectedProject.title}
//                   className="w-full h-64 object-cover rounded-t-3xl"
//                 />
//                 <button
//                   onClick={() => setSelectedProject(null)}
//                   className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:text-brandBlue transition-all duration-300"
//                 >
//                   <svg
//                     className="w-5 h-5"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M6 18L18 6M6 6l12 12"
//                     />
//                   </svg>
//                 </button>
//               </div>

//               {/* Modal Content */}
//               <div className="p-8">
//                 <div className="flex items-center space-x-3 mb-4">
//                   <span className="px-3 py-1 bg-brandBlue text-white text-sm font-semibold rounded-full">
//                     {
//                       categories.find(
//                         (cat) => cat.id === selectedProject.category
//                       )?.name
//                     }
//                   </span>
//                   <span className="px-3 py-1 bg-green-500 text-white text-sm font-semibold rounded-full">
//                     {selectedProject.status}
//                   </span>
//                 </div>

//                 <h2 className="text-3xl font-bold text-primaryTextColor mb-4 font-lexend">
//                   {selectedProject.title}
//                 </h2>

//                 <p className="text-secondaryTextColor text-lg leading-relaxed mb-6">
//                   {selectedProject.description}
//                 </p>

//                 {/* Project Details Grid */}
//                 <div className="grid md:grid-cols-2 gap-6 mb-8">
//                   <div className="space-y-4">
//                     <div className="flex items-center space-x-3">
//                       <Users className="w-5 h-5 text-brandBlue" />
//                       <div>
//                         <div className="text-sm text-secondaryTextColor">
//                           Client
//                         </div>
//                         <div className="font-semibold text-primaryTextColor">
//                           {selectedProject.client}
//                         </div>
//                       </div>
//                     </div>
//                     <div className="flex items-center space-x-3">
//                       <MapPin className="w-5 h-5 text-brandBlue" />
//                       <div>
//                         <div className="text-sm text-secondaryTextColor">
//                           Location
//                         </div>
//                         <div className="font-semibold text-primaryTextColor">
//                           {selectedProject.location}
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="space-y-4">
//                     <div className="flex items-center space-x-3">
//                       <Calendar className="w-5 h-5 text-brandBlue" />
//                       <div>
//                         <div className="text-sm text-secondaryTextColor">
//                           Duration
//                         </div>
//                         <div className="font-semibold text-primaryTextColor">
//                           {selectedProject.duration}
//                         </div>
//                       </div>
//                     </div>
//                     <div className="flex items-center space-x-3">
//                       <Target className="w-5 h-5 text-brandBlue" />
//                       <div>
//                         <div className="text-sm text-secondaryTextColor">
//                           Completed
//                         </div>
//                         <div className="font-semibold text-primaryTextColor">
//                           {selectedProject.completion}
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Technologies */}
//                 <div className="mb-8">
//                   <h3 className="text-xl font-bold text-primaryTextColor mb-4 font-lexend">
//                     Technologies Used
//                   </h3>
//                   <div className="flex flex-wrap gap-2">
//                     {selectedProject.technologies.map((tech, index) => (
//                       <span
//                         key={index}
//                         className="px-4 py-2 bg-gray-100 text-secondaryTextColor font-medium rounded-xl"
//                       >
//                         {tech}
//                       </span>
//                     ))}
//                   </div>
//                 </div>

//                 {/* Gallery Preview */}
//                 <div className="mb-8">
//                   <h3 className="text-xl font-bold text-primaryTextColor mb-4 font-lexend">
//                     Project Gallery
//                   </h3>
//                   <div className="grid grid-cols-2 gap-4">
//                     {selectedProject.gallery.slice(0, 2).map((image, index) => (
//                       <img
//                         key={index}
//                         src={image}
//                         alt={`${selectedProject.title} ${index + 1}`}
//                         className="w-full h-32 object-cover rounded-xl"
//                       />
//                     ))}
//                   </div>
//                 </div>

//                 {/* Action Buttons */}
//                 <div className="flex space-x-4">
//                   <motion.button
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                     className="px-6 py-3 bg-brandBlue text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
//                   >
//                     <Download className="w-5 h-5" />
//                     <span>Download Case Study</span>
//                   </motion.button>
//                   <motion.button
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                     className="px-6 py-3 bg-white text-brandBlue font-semibold rounded-xl border-2 border-brandBlue hover:bg-brandBlue hover:text-white transition-all duration-300"
//                   >
//                     Similar Projects
//                   </motion.button>
//                 </div>
//               </div>
//             </motion.div>
//           </motion.div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectsPage;
